import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  Share2,
  Layers,
  Briefcase,
  Camera,
  Music,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';

/* ── Brand palette ────────────────────────────────── */
const NAVY   = '#0F1C2E';
const PURPLE = '#7D468C';
const TEAL   = '#4EBFBF';
const CORAL  = '#F2785C';

/* ── Service data ─────────────────────────────────── */
const SERVICES = [
  {
    key: 'digital_marketing',
    icon: TrendingUp,
    accent: TEAL,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&q=80',
    tags: ['SEO', 'SEM', 'Analytics', 'Ads'],
  },
  {
    key: 'social_media',
    icon: Share2,
    accent: PURPLE,
    image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=900&q=80',
    tags: ['Instagram', 'Facebook', 'TikTok', 'LinkedIn'],
  },
  {
    key: 'branding',
    icon: Layers,
    accent: CORAL,
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=900&q=80',
    tags: ['Logo', 'Identity', 'Guidelines', 'Typography'],
  },
  {
    key: 'project_management',
    icon: Briefcase,
    accent: TEAL,
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=900&q=80',
    tags: ['Strategy', 'Planning', 'Execution', 'Reporting'],
  },
  {
    key: 'content_creation',
    icon: Camera,
    accent: PURPLE,
    image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=900&q=80',
    tags: ['Photo', 'Video', 'Copy', 'Design'],
  },
  {
    key: 'event_production',
    icon: Music,
    accent: CORAL,
    image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=900&q=80',
    tags: ['Live Events', 'Virtual', 'Coordination', 'AV'],
  },
];

/* ── i18n fallback labels (used if key not in locale) */
const FALLBACK_LABELS = {
  digital_marketing:  { title: 'Digital Marketing',    desc: 'Data-driven strategies that grow your reach, attract the right audience, and turn traffic into real results.' },
  social_media:       { title: 'Social Media',          desc: 'Compelling content and community management across every platform to keep your brand top of mind.' },
  branding:           { title: 'Branding & Identity',   desc: 'From logo to brand guidelines, we craft a visual identity that sets you apart and builds trust.' },
  project_management: { title: 'Project Management',    desc: 'End-to-end coordination of your marketing initiatives — on time, on budget, and on brand.' },
  content_creation:   { title: 'Content Creation',      desc: 'Photography, video, copy, and design that tells your story and engages your audience at every touchpoint.' },
  event_production:   { title: 'Event Production',      desc: 'From intimate brand activations to large-scale productions, we handle every detail so you can focus on the moment.' },
};

/* ── Main Component ───────────────────────────────── */
export default function ServicesSelector() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedMobile, setExpandedMobile] = useState(null);
  const imageRef = useRef(null);

  const active = SERVICES[activeIndex];

  const getTitle = (key) =>
    t(`services_selector.${key}.title`, { defaultValue: FALLBACK_LABELS[key].title });

  const getDesc = (key) =>
    t(`services_selector.${key}.desc`, { defaultValue: FALLBACK_LABELS[key].desc });

  const sectionLabel = t('services_selector.heading', { defaultValue: 'What We Do' });
  const ctaLabel     = t('services_selector.cta',     { defaultValue: 'Start a project' });
  const exploreLabel = t('services_selector.explore', { defaultValue: 'Explore service' });

  /* smooth image swap */
  const handleSelect = (idx) => {
    if (idx === activeIndex) return;
    if (imageRef.current) {
      imageRef.current.style.opacity = '0';
      imageRef.current.style.transform = 'scale(1.04)';
    }
    setTimeout(() => {
      setActiveIndex(idx);
      if (imageRef.current) {
        imageRef.current.style.opacity = '1';
        imageRef.current.style.transform = 'scale(1)';
      }
    }, 220);
  };

  return (
    <section
      id="servicios"
      className="relative py-20 overflow-hidden"
      style={{ background: `linear-gradient(160deg, ${NAVY} 0%, #192840 60%, #1a1030 100%)` }}
    >
      {/* Background blobs */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background: `
            radial-gradient(ellipse 55% 45% at 15% 25%, ${PURPLE}22 0%, transparent 70%),
            radial-gradient(ellipse 45% 40% at 85% 70%, ${TEAL}18 0%, transparent 70%),
            radial-gradient(ellipse 35% 30% at 50% 90%, ${CORAL}12 0%, transparent 70%)
          `,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">

        {/* ── Section header ── */}
        <div className="mb-14 text-center">
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-4 py-1 rounded-full mb-4"
            style={{
              background: `${TEAL}20`,
              color: TEAL,
              border: `1px solid ${TEAL}40`,
            }}
          >
            {sectionLabel}
          </span>
          <h2
            className="text-4xl md:text-5xl font-extrabold leading-tight"
            style={{
              background: `linear-gradient(135deg, #fff 0%, #c8b4d4 50%, ${TEAL} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('services_selector.title', { defaultValue: 'Our Services' })}
          </h2>
          <p
            className="mt-4 text-base md:text-lg max-w-2xl mx-auto"
            style={{ color: 'rgba(255,255,255,0.55)' }}
          >
            {t('services_selector.subtitle', {
              defaultValue:
                'We offer a full suite of creative and strategic marketing services tailored to grow your brand.',
            })}
          </p>
        </div>

        {/* ── DESKTOP: two-column layout ── */}
        <div className="hidden lg:grid grid-cols-2 gap-10 items-start">

          {/* Left — service list */}
          <div className="flex flex-col gap-2">
            {SERVICES.map((svc, idx) => {
              const Icon    = svc.icon;
              const isActive = idx === activeIndex;
              return (
                <button
                  key={svc.key}
                  onClick={() => handleSelect(idx)}
                  className="group w-full text-left rounded-2xl px-5 py-4 flex items-center gap-4 transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300 focus:outline-none"
                  style={{
                    background: isActive
                      ? `linear-gradient(120deg, ${PURPLE}30, ${TEAL}18)`
                      : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${isActive ? svc.accent + '70' : 'rgba(255,255,255,0.07)'}`,
                    boxShadow: isActive ? `0 4px 24px ${svc.accent}20` : 'none',
                  }}
                >
                  {/* Icon bubble */}
                  <span
                    className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300"
                    style={{
                      background: isActive ? `${svc.accent}25` : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${isActive ? svc.accent + '50' : 'transparent'}`,
                    }}
                  >
                    <Icon
                      size={20}
                      style={{ color: isActive ? svc.accent : 'rgba(255,255,255,0.4)' }}
                    />
                  </span>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-semibold text-sm truncate transition-colors duration-300"
                      style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.65)' }}
                    >
                      {getTitle(svc.key)}
                    </p>
                    {isActive && (
                      <p
                        className="text-xs mt-0.5 line-clamp-2"
                        style={{ color: 'rgba(255,255,255,0.5)' }}
                      >
                        {getDesc(svc.key)}
                      </p>
                    )}
                  </div>

                  {/* Arrow */}
                  <ArrowRight
                    size={16}
                    className="flex-shrink-0 transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300"
                    style={{
                      color: isActive ? svc.accent : 'transparent',
                      transform: isActive ? 'translateX(0)' : 'translateX(-4px)',
                    }}
                  />
                </button>
              );
            })}

            {/* CTA */}
            <a
              href="/contacto"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold text-sm transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300 hover:gap-3"
              style={{
                background: `linear-gradient(135deg, ${CORAL}, ${PURPLE})`,
                color: '#fff',
                boxShadow: `0 6px 28px ${CORAL}40`,
              }}
            >
              {ctaLabel}
              <ArrowRight size={16} />
            </a>
          </div>

          {/* Right — image + details card */}
          <div className="sticky top-24">
            {/* Image */}
            <div
              className="relative rounded-3xl overflow-hidden"
              style={{
                height: '340px',
                border: `1px solid ${active.accent}30`,
                boxShadow: `0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px ${active.accent}15`,
              }}
            >
              <img
                ref={imageRef}
                src={active.image}
                alt={getTitle(active.key)}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  transition: 'opacity 0.22s ease, transform 0.22s ease',
                }}
              />
              {/* Gradient overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(to top, ${NAVY}ee 0%, ${NAVY}40 50%, transparent 100%)`,
                }}
              />

              {/* Tags */}
              <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                {active.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium px-3 py-1 rounded-full"
                    style={{
                      background: `${active.accent}25`,
                      color: active.accent,
                      border: `1px solid ${active.accent}40`,
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Details card — liquid glass */}
            <div
              className="mt-4 rounded-2xl p-6"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid rgba(255,255,255,0.10)`,
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${active.accent}20`, border: `1px solid ${active.accent}40` }}
                >
                  <active.icon size={18} style={{ color: active.accent }} />
                </span>
                <h3 className="font-bold text-white text-lg">{getTitle(active.key)}</h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {getDesc(active.key)}
              </p>
              <Link
                to={`/servicios/${active.key}`}
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-200 hover:gap-3"
                style={{ color: active.accent }}
              >
                {exploreLabel} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        {/* ── MOBILE: accordion ── */}
        <div className="flex flex-col gap-3 lg:hidden">
          {SERVICES.map((svc, idx) => {
            const Icon      = svc.icon;
            const isOpen    = expandedMobile === idx;
            return (
              <div
                key={svc.key}
                className="rounded-2xl overflow-hidden transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300"
                style={{
                  background: isOpen
                    ? `linear-gradient(120deg, ${PURPLE}25, ${TEAL}15)`
                    : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isOpen ? svc.accent + '60' : 'rgba(255,255,255,0.07)'}`,
                }}
              >
                <button
                  onClick={() => setExpandedMobile(isOpen ? null : idx)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left focus:outline-none"
                >
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: isOpen ? `${svc.accent}20` : 'rgba(255,255,255,0.06)',
                      border: `1px solid ${isOpen ? svc.accent + '40' : 'transparent'}`,
                    }}
                  >
                    <Icon size={18} style={{ color: isOpen ? svc.accent : 'rgba(255,255,255,0.45)' }} />
                  </span>
                  <span
                    className="flex-1 font-semibold text-sm"
                    style={{ color: isOpen ? '#fff' : 'rgba(255,255,255,0.7)' }}
                  >
                    {getTitle(svc.key)}
                  </span>
                  <ChevronDown
                    size={16}
                    style={{
                      color: svc.accent,
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      opacity: isOpen ? 1 : 0.4,
                    }}
                  />
                </button>

                {isOpen && (
                  <div className="px-5 pb-5">
                    <div
                      className="rounded-xl overflow-hidden mb-4"
                      style={{ height: '200px' }}
                    >
                      <img
                        src={svc.image}
                        alt={getTitle(svc.key)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-sm leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {getDesc(svc.key)}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {svc.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs font-medium px-3 py-1 rounded-full"
                          style={{
                            background: `${svc.accent}20`,
                            color: svc.accent,
                            border: `1px solid ${svc.accent}40`,
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Link
                      to={`/servicios/${svc.key}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold"
                      style={{ color: svc.accent }}
                    >
                      {exploreLabel} <ArrowRight size={13} />
                    </Link>
                  </div>
                )}
              </div>
            );
          })}

          {/* Mobile CTA */}
          <a
            href="/contacto"
            className="mt-2 flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-semibold text-sm text-white transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300"
            style={{
              background: `linear-gradient(135deg, ${CORAL}, ${PURPLE})`,
              boxShadow: `0 6px 24px ${CORAL}35`,
            }}
          >
            {ctaLabel}
            <ArrowRight size={15} />
          </a>
        </div>
      </div>
    </section>
  );
}
